import axios from 'axios';

const REACT_APP_BFF_URL = process.env.REACT_APP_BFF_URL;

export const generateDynamicPDF = async (token, separado) => {
    try {
        // Retrieve and parse report data from sessionStorage
        const reportString = sessionStorage.getItem("report");
        if (!reportString) {
            throw new Error("No report data found in session.");
        }

        let report;
        try {
            report = JSON.parse(reportString);
        } catch (parseError) {
            throw new Error("Invalid report data format.");
        }

        const {id} = report;

        if (!id) {
            throw new Error("Incomplete report data.");
        }

        // Make the POST request to generate the PDF
        const response = await fetch(`${REACT_APP_BFF_URL}/generate-pdf`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                id: id,
                newPageDiagnoses: separado
            }),
        });

        if (!response.ok) {
            let errorMessage = 'Error occurred while generating PDF.';
            try {
                const errorData = await response.json();
                errorMessage = errorData.detail || errorMessage;
            } catch (jsonError) {
                // If response is not JSON, retain the default error message
            }
            throw new Error(errorMessage);
        }

        // Handle the binary PDF data
        const blob = await response.blob();

        // Create a temporary URL for the blob
        const url = window.URL.createObjectURL(new Blob([blob], {type: 'application/pdf'}));

        const metadataResponse = await axios.get(`${process.env.REACT_APP_BFF_URL}/reports/${id}/pdf-metadata`, {
            headers: {
                "Authorization": `Bearer ${token}`
            },
            responseType: 'json'
        });

        const {filename} = metadataResponse.data;

        if (!filename) {
            throw new Error("Filename not provided in metadata.");
        }

        // Create a temporary anchor element to initiate the download
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${filename}`);

        // Append the anchor to the document body and trigger a click
        document.body.appendChild(link);
        link.click();

        // Clean up by removing the anchor and revoking the object URL
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

    } catch (error) {
        console.error('Error generating PDF:', error);
        throw error; // Rethrow the error to ensure it propagates to the caller
    }
};

export const createReport = async (token, formData) => {
    try {
        // Send the POST request using Axios
        const response = await axios.post(`${process.env.REACT_APP_BFF_URL}/reports`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${token}`,
            }
        });


        return response.data;
    } catch (error) {
        // Handle errors appropriately
        if (error.response && error.response.data) {
            // Backend returned an error response
            throw new Error(error.response.data.detail || 'Error occurred while creating report.');
        } else {
            // Network or other errors
            throw new Error(error.message || 'Error occurred while creating report.');
        }
    }
};

export const addRequest = async (token, requestId) => {
    try {
        const requestData = JSON.parse(sessionStorage.getItem("formData2")).redaccion;

        const response = await axios.put(
            `${process.env.REACT_APP_BFF_URL}/reports/${requestId}/requests`,
            {request: requestData}, // Directly send the payload
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        // Handle errors appropriately
        if (error.response && error.response.data) {
            // Backend returned an error response
            throw new Error(error.response.data.detail || 'Error occurred while creating request.');
        } else {
            // Network or other errors
            throw new Error(error.message || 'Error occurred while creating request.');
        }
    }
};

export const addDiagnosis = async (token, requestId) => {
    try {
        const diagnosisData = JSON.parse(sessionStorage.getItem("vertexResponse"));

        const response = await axios.put(
            `${process.env.REACT_APP_BFF_URL}/reports/${requestId}/diagnosis`,
            {diagnosis: diagnosisData}, // Directly send the payload
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        // Handle errors appropriately
        if (error.response && error.response.data) {
            // Backend returned an error response
            throw new Error(error.response.data.detail || 'Error occurred while creating diagnosis.');
        } else {
            // Network or other errors
            throw new Error(error.message || 'Error occurred while creating diagnosis.');
        }
    }
};

export const addFindings = async (token, requestId) => {
    try {
        const findingsData = JSON.parse(sessionStorage.getItem("formData2")).observations;

        const response = await axios.post(
            `${process.env.REACT_APP_BFF_URL}/reports/${requestId}/findings`,
            {findings: findingsData}, // Directly send the payload
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        // Handle errors appropriately
        if (error.response && error.response.data) {
            // Backend returned an error response
            throw new Error(error.response.data.detail || 'Error occurred while creating findings.');
        } else {
            // Network or other errors
            throw new Error(error.message || 'Error occurred while creating findings.');
        }
    }
};

export const updateImages = async (token, requestId) => {
    try {
        const imagesBase64 = JSON.parse(sessionStorage.getItem("images")); // Asegúrate de que sea un array

        // If no images, return early with success
        if (!imagesBase64 || imagesBase64.length === 0) {
            return { message: "No images to update" };
        }

        const formData = new FormData();

        // Convertir las imágenes base64 a archivos Blob y agregarlas al FormData
        imagesBase64.forEach((imageBase64, index) => {
            // Split the base64 string to get the data and the file type
            const [metadata, data] = imageBase64.split(',');
            const mime = metadata.match(/:(.*?);/)[1];

            // Convertir la cadena base64 a un Blob
            const byteString = atob(data);
            const byteNumbers = new Array(byteString.length);
            for (let i = 0; i < byteString.length; i++) {
                byteNumbers[i] = byteString.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mime });

            // Agregar el archivo Blob al FormData
            formData.append("images", blob, `image_${index}.${mime.split('/')[1]}`);
        });

        const response = await axios.put(
            `${process.env.REACT_APP_BFF_URL}/reports/${requestId}/images`,
            formData, // Enviar el FormData en lugar del JSON
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        // Manejar los errores adecuadamente
        if (error.response && error.response.data) {
            // Backend returned an error response
            throw new Error(error.response.data.detail || 'Error occurred while updating images.');
        } else {
            // Network or other errors
            throw new Error(error.message || 'Error occurred while updating images.');
        }
    }
};


export const updateReport = async (token, reportId, formData) => {
    try {
        // Send the PUT request using Axios
        const response = await axios.put(`${process.env.REACT_APP_BFF_URL}/reports/${reportId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${token}`,
            }
        });


        return response.data;
    } catch (error) {
        // Handle errors appropriately
        if (error.response && error.response.data) {
            // Backend returned an error response
            throw new Error(error.response.data.detail || 'Error occurred while updating report.');
        } else {
            // Network or other errors
            throw new Error(error.message || 'Error occurred while updating report.');
        }
    }

};

export const getReportsByVeterinary = async (token) => {
    try {
        const response = await axios.get(`${REACT_APP_BFF_URL}/reports/my-veterinary`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            }
        });

        return response.data;
    } catch (error) {
        if (error.response && error.response.data) {
            // Backend returned an error response
            throw new Error(error.response.data.detail || 'Error occurred while getting reports by veterinary.');
        } else {
            // Network or other errors
            throw new Error(error.message || 'Error occurred while getting reports by veterinary');
        }
    }
};

export const addNotes = async (token, reportId, notes) => {
    try {
        const response = await axios.post(
            `${process.env.REACT_APP_BFF_URL}/reports/${reportId}/notes`,
            {notes: notes},
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        // Handle errors appropriately
        if (error.response && error.response.data) {
            // Backend returned an error response
            throw new Error(error.response.data.detail || 'Error occurred while creating findings.');
        } else {
            // Network or other errors
            throw new Error(error.message || 'Error occurred while creating findings.');
        }
    }

};

export const downloadPDF = async (token, reportId) => {
    try {
        // Step 1: Fetch metadata to get the filename
        const metadataResponse = await axios.get(`${process.env.REACT_APP_BFF_URL}/reports/${reportId}/pdf-metadata`, {
            headers: {
                "Authorization": `Bearer ${token}`
            },
            responseType: 'json'
        });

        const {filename} = metadataResponse.data;

        // Step 2: Download the PDF binary data
        const pdfResponse = await axios.get(`${process.env.REACT_APP_BFF_URL}/reports/${reportId}/pdf`, {
            headers: {
                "Authorization": `Bearer ${token}`
            },
            responseType: 'blob' // Handle binary data
        });

        const blob = new Blob([pdfResponse.data], {type: 'application/pdf'});

        // Create a link element for download
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.download = filename;

        // Append to the document and trigger download
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

    } catch (error) {
        if (error.response && error.response.data) {
            // Backend returned an error response
            const errorMessage = error.response.data.detail || 'Error occurred while downloading PDF.';
            console.error(errorMessage);
            // Optionally, display the error to the user using a notification system
        } else {
            // Network or other errors
            console.error(error.message || 'Error occurred while downloading PDF.');
            // Optionally, display the error to the user using a notification system
        }
    }
};

export const duplicateReportPartial = async (token, reportId) => {
    try {
        const response = await axios.post(`${process.env.REACT_APP_BFF_URL}/reports/${reportId}/duplicate-partial`, {}, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }
        });

        return response.data;
    } catch (error) {
        if (error.response && error.response.data) {
            // Backend returned an error response
            throw new Error(error.response.data.detail || 'Error occurred while duplicating report.');
        } else {
            // Network or other errors
            throw new Error(error.message || 'Error occurred while duplicating report.');
        }
    }
};

export const duplicateReportComplete = async (token, reportId) => {
    try {
        const response = await axios.post(`${process.env.REACT_APP_BFF_URL}/reports/${reportId}/duplicate`, {}, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }
        });

        return response.data;
    } catch (error) {
        if (error.response && error.response.data) {
            // Backend returned an error response
            throw new Error(error.response.data.detail || 'Error occurred while duplicating report.');
        } else {
            // Network or other errors
            throw new Error(error.message || 'Error occurred while duplicating report.');
        }
    }
};

export const deleteReport = async (token, reportId) => {
    try {
        const response = await axios.delete(`${process.env.REACT_APP_BFF_URL}/reports/${reportId}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }
        });

        return response.data;
    } catch (error) {
        if (error.response && error.response.data) {
            // Backend returned an error response
            throw new Error(error.response.data.detail || 'Error occurred while deleting report.');
        } else {
            // Network or other errors
            throw new Error(error.message || 'Error occurred while deleting report.');
        }
    }
};

export const getReportImages = async (token, reportId) => {
    try {
        const response = await axios.get(`${REACT_APP_BFF_URL}/reports/${reportId}/images`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            }
        });

        return response.data;
    } catch (error) {
        if (error.response && error.response.data) {
            throw new Error(error.response.data.detail || 'Error occurred while getting report images.');
        } else {
            throw new Error(error.message || 'Error occurred while getting report images.');
        }
    }
};

export const saveReportDetails = async (token, reportId) => {
    try {
      const observationsForm = JSON.parse(sessionStorage.getItem("observationsForm") || "{}")
      const formData2 = JSON.parse(sessionStorage.getItem("formData2") || "{}")
      const vertexResponse = JSON.parse(sessionStorage.getItem("vertexResponse") || "{}")
  
      const findings = observationsForm.observations || []
      const request = formData2.redaccion || ""
  
      const diagnosis = vertexResponse && Object.keys(vertexResponse).length > 0
        ? vertexResponse
        : {
            aiGenerated: "",
            finalDiagnosis: "",
            generatedDiagnosis: "",
            model: "",
            modified: "",
            prompt: ""
          }
  
      const updateData = {
        findings,
        requests: request,
        diagnosis,
      }
      console.log(updateData)
  
      const response = await axios.put(`${process.env.REACT_APP_BFF_URL}/reports/${reportId}/save`, updateData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
  
      return response.data
    } catch (error) {
      if (error.response && error.response.data) {
        throw new Error(error.response.data.detail || "Error occurred while updating report details.")
      } else {
        throw new Error(error.message || "Error occurred while updating report details.")
      }
    }
  }
  
  
  export const getAllReportsByVeterinary = async (token, page = 1, pageSize = 50) => {
    try {
      const response = await axios.get(`${REACT_APP_BFF_URL}/reports/get_all_by_vet`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        params: {
          page,
          page_size: pageSize
        }
      });
  
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        throw new Error(error.response.data.detail || 'Error occurred while getting reports by veterinary.');
      } else {
        throw new Error(error.message || 'Error occurred while getting reports by veterinary');
      }
    }
  };

  export const getAllVeterinariesStats = async (token) => {
    try {
      const response = await axios.get(`${REACT_APP_BFF_URL}/reports/stats`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
  
      return response.data
    } catch (error) {
      if (error.response && error.response.data) {
        throw new Error(error.response.data.detail || "Error occurred while getting veterinary statistics.")
      } else {
        throw new Error(error.message || "Error occurred while getting veterinary statistics")
      }
    }
  }